/**
 * iter-156dc — Frontend error reporter.
 *
 * Captures JS runtime exceptions (Option A from the planning
 * discussion — NOT axios 4xx/5xx, which would overlap with the
 * backend's existing `endpoint_metrics`):
 *
 *   1. React component crashes via `<ErrorBoundary>` below.
 *   2. `window.error` — synchronous throws not caught by any handler.
 *   3. `window.unhandledrejection` — `async` code whose Promise
 *      rejection nobody awaited.
 *
 * All three POST to `/api/system-logs/client-error` which writes a
 * `category: "client_error"` row to `db.system_logs`. The platform-
 * admin System Logs tab surfaces them.
 *
 * Throttling: an in-memory Map keyed by
 * `(source, message[:80], page)` blocks duplicate sends within a
 * 5-minute window. A broken thumbnail in a render loop emits ONE
 * row, not 1000.
 *
 * NEVER throws — a failure to report an error must not itself
 * cause more errors. Every codepath here is wrapped in try/catch.
 */
import React from "react";
import axios from "axios";
import { isChunkLoadError, reloadOnceForChunkError } from "@/lib/lazyWithRetry";

const THROTTLE_MS = 5 * 60 * 1000;          // 5 minutes
const _recentSends = new Map();             // key -> timestamp ms

function _shouldSend(key) {
  try {
    const now = Date.now();
    const last = _recentSends.get(key);
    if (last && now - last < THROTTLE_MS) return false;
    _recentSends.set(key, now);
    // Soft cap so the map doesn't grow unbounded over a long
    // session. 256 distinct error keys is well above the ceiling
    // of "actual distinct bugs an SPA could plausibly emit".
    if (_recentSends.size > 256) {
      const cutoff = now - THROTTLE_MS;
      for (const [k, t] of _recentSends) {
        if (t < cutoff) _recentSends.delete(k);
      }
    }
    return true;
  } catch {
    return true;     // dedup failure → allow the send
  }
}

function _post({ source, message, stack }) {
  try {
    const page = `${window.location.pathname}${window.location.search}`;
    const key = `${source}::${(message || "").slice(0, 80)}::${page}`;
    if (!_shouldSend(key)) return;
    // axios is already configured with withCredentials elsewhere
    // via a default; if not we explicitly include it so the auth
    // cookie travels with the report.
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/system-logs/client-error`;
    axios.post(url, {
      source,
      message: (message || "").slice(0, 500),
      stack: (stack || "").slice(0, 4000),
      page,
      user_agent: (navigator && navigator.userAgent) || "",
    }, { withCredentials: true }).catch(() => { /* swallow */ });
  } catch {
    /* swallow: never throw from the reporter */
  }
}

/** Report a client-side issue that didn't throw (same throttled `client_error` row). */
export function reportClientError(source, message, stack = "") {
  _post({ source, message, stack });
}

// Browser page translation desynced React's DOM ("removeChild / insertBefore … not a child", Safari "The object can
// not be found here"). The translate guard normally prevents it; if one still slips through, re-render instead.
const _DOM_DESYNC = /not a child of this node|can ?not be found here/i;
export function isDomDesyncError(error) {
  const msg = `${(error && error.name) || ""} ${(error && error.message) || ""}`;
  return _DOM_DESYNC.test(msg);
}
const MAX_DOM_RECOVERIES = 3;

/**
 * Install window-level listeners. Idempotent — calling install()
 * twice still results in one set of listeners.
 */
let _installed = false;
export function installClientErrorReporter() {
  if (_installed) return;
  _installed = true;
  if (typeof window === "undefined") return;
  try {
    window.addEventListener("error", (e) => {
      // The ResizeObserver-loop swallow installed earlier in
      // index.js calls stopImmediatePropagation in its own
      // capture-phase listener, so this bubble-phase listener
      // never fires for that benign warning.
      const err = e && e.error;
      // Post-deploy stale-chunk recovery: force a one-time reload so
      // the browser fetches the fresh chunk manifest.
      if (reloadOnceForChunkError(err || e)) return;
      const msg = (e && e.message) || "Unknown error";
      const stack = e && e.error && e.error.stack ? e.error.stack : "";
      _post({ source: "window_error", message: msg, stack });
    });
    window.addEventListener("unhandledrejection", (e) => {
      const reason = e && e.reason;
      if (reloadOnceForChunkError(reason)) return;
      const msg =
        (reason && (reason.message || String(reason))) ||
        "Unhandled promise rejection";
      const stack = (reason && reason.stack) || "";
      _post({ source: "unhandled_rejection", message: msg, stack });
    });
  } catch {
    /* swallow */
  }
}

/**
 * React error boundary. Wrap the app once near the root so
 * component-render crashes get reported AND the user sees a
 * non-blank fallback rather than a white page.
 */
export class ClientErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: "", resetKey: 0 };
    this.domRecoveries = 0;
  }

  static getDerivedStateFromError(error) {
    // Post-deploy stale-chunk recovery: if a render crashed because a
    // lazy chunk couldn't load, reload once to pull the fresh manifest
    // instead of showing the fallback.
    if (isChunkLoadError(error) && reloadOnceForChunkError(error)) {
      return { hasError: false, errorMessage: "" };
    }
    return {
      hasError: true,
      errorMessage: (error && (error.message || String(error))) || "Render error",
    };
  }

  componentDidCatch(error, info) {
    // Don't report a chunk error we're auto-recovering from.
    if (isChunkLoadError(error)) return;
    const recover = isDomDesyncError(error) && this.domRecoveries < MAX_DOM_RECOVERIES;
    _post({
      source: recover ? "error_boundary_recovered" : "error_boundary",
      message: (error && (error.message || String(error))) || "Render error",
      stack: (error && error.stack ? error.stack + "\n\n" : "") +
             (info && info.componentStack ? info.componentStack : ""),
    });
    if (recover) {
      this.domRecoveries += 1;
      this.setState((s) => ({ hasError: false, errorMessage: "", resetKey: s.resetKey + 1 }));
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          data-testid="client-error-boundary-fallback"
          style={{
            maxWidth: 560, margin: "10vh auto", padding: "32px 24px",
            fontFamily: "system-ui, sans-serif", color: "#111",
            background: "#FFFFFF", border: "1px solid #E5E7EB",
            borderRadius: 8,
          }}
        >
          <h1 style={{ fontSize: 20, margin: 0, marginBottom: 8 }}>
            Something went wrong on this page.
          </h1>
          <p style={{ fontSize: 14, color: "#475569", marginTop: 0 }}>
            The error has been reported. You can refresh, or use the
            navigation to go elsewhere.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              marginTop: 16, padding: "8px 16px", border: "1px solid #D5D8DD",
              background: "#FFFFFF", borderRadius: 6, fontSize: 14, cursor: "pointer",
            }}
          >
            Reload page
          </button>
        </div>
      );
    }
    return <React.Fragment key={this.state.resetKey}>{this.props.children}</React.Fragment>;
  }
}
